import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import toast from 'react-hot-toast';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';

const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <Navbar />
        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-20 text-center">
          <ShoppingBag className="w-24 h-24 text-zinc-700 mx-auto mb-6" />
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight uppercase font-heading mb-4">
            Your Cart is <span className="text-lime-400">Empty</span>
          </h1>
          <p className="text-zinc-400 mb-8">Add some premium briquettes to get started!</p>
          <Link
            to="/products"
            className="inline-block bg-lime-400 text-black hover:bg-lime-500 font-bold uppercase tracking-wider px-8 py-4 shadow-[4px_4px_0px_0px_#ffffff] transition-all hover:-translate-y-1"
            data-testid="empty-cart-shop-btn"
          >
            Shop Products
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-16">
        <h1 className="text-6xl md:text-8xl font-bold tracking-tighter uppercase leading-[0.9] mb-8 font-heading">
          Your <span className="text-lime-400">Cart</span>
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <div
                key={item.id}
                className="bg-zinc-900 border border-zinc-800 p-6 flex gap-6 items-center hover:border-lime-400/50 transition-colors"
                data-testid={`cart-item-${item.id}`}
              >
                <img
                  src={item.image_url}
                  alt={item.name}
                  className="w-24 h-24 object-cover border border-zinc-800"
                />
                <div className="flex-1">
                  <h3 className="text-xl font-semibold tracking-tight font-heading uppercase mb-1">
                    {item.name}
                  </h3>
                  <p className="text-zinc-400 text-sm mb-2">{item.weight_kg}kg bag</p>
                  <p className="text-2xl font-bold text-lime-400">₹{item.price}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    className="w-8 h-8 bg-zinc-800 hover:bg-zinc-700 text-white font-bold border border-zinc-700"
                    data-testid={`decrease-qty-${item.id}`}
                  >
                    <Minus className="w-4 h-4 mx-auto" />
                  </button>
                  <span className="w-12 h-8 bg-zinc-950 border border-zinc-800 flex items-center justify-center font-bold" data-testid={`qty-${item.id}`}>
                    {item.quantity}
                  </span>
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    className="w-8 h-8 bg-zinc-800 hover:bg-zinc-700 text-white font-bold border border-zinc-700"
                    data-testid={`increase-qty-${item.id}`}
                  >
                    <Plus className="w-4 h-4 mx-auto" />
                  </button>
                </div>
                <button
                  onClick={() => {
                    removeFromCart(item.id);
                    toast.success('Item removed from cart');
                  }}
                  className="p-2 hover:bg-zinc-800 text-zinc-400 hover:text-red-500 transition-colors"
                  data-testid={`remove-item-${item.id}`}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))}

            <button
              onClick={() => {
                clearCart();
                toast.success('Cart cleared');
              }}
              className="text-zinc-400 hover:text-red-500 text-sm uppercase tracking-wider transition-colors"
              data-testid="clear-cart-btn"
            >
              Clear Cart
            </button>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-zinc-900 border border-zinc-800 p-6 sticky top-24">
              <h2 className="font-heading text-2xl font-bold uppercase mb-6">Order Summary</h2>
              
              <div className="space-y-3 mb-6">
                {cart.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-zinc-400">
                      {item.name} x {item.quantity}
                    </span>
                    <span className="text-white font-semibold">₹{item.price * item.quantity}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-zinc-800 pt-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-xl font-semibold uppercase">Total</span>
                  <span className="text-3xl font-bold text-lime-400" data-testid="cart-total">
                    ₹{getCartTotal()}
                  </span>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                className="w-full bg-lime-400 text-black hover:bg-lime-500 font-bold uppercase tracking-wider px-8 py-4 shadow-[4px_4px_0px_0px_#ffffff] transition-all hover:-translate-y-1 flex items-center justify-center gap-2"
                data-testid="proceed-checkout-btn"
              >
                Proceed to Checkout
                <ArrowRight className="w-5 h-5" />
              </button>

              <Link
                to="/products"
                className="block w-full mt-4 bg-transparent border-2 border-zinc-700 text-white hover:border-lime-400 hover:text-lime-400 font-bold uppercase tracking-wider px-8 py-4 transition-colors text-center"
                data-testid="continue-shopping-btn"
              >
                Continue Shopping
              </Link>

              <div className="mt-6 p-4 bg-lime-400/10 border border-lime-400/20">
                <p className="text-lime-400 font-semibold text-sm mb-1">✅ Same-day delivery</p>
                <p className="text-zinc-400 text-xs">Available for all Bengaluru pin codes</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CartPage;
